import { MockServiceGroup } from './mockServiceGroupTypes';
import { AnyAction } from 'redux';
import { ApplicationState } from '../rootReducer';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { MockServiceActionTypes } from '../mockServices/mockServiceActionTypes';
import i18n from '../../i18n';
import { generateDate } from '../../utils/dateUtil';
import { BlazeLocation } from '../mockServices/mockServiceActions';
import { createGroup, deleteGroup, setGroupsIsLoading, updateGroup, updateGroupList } from './mockServiceGroupReducer';
import _ from 'lodash';
import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import * as actionTypes from '../../constants/action-types';
import acApi from '../../axios/ac/acApi';
import { TRANSACTIONAL_LOCATIONS_ID_SUFFIX } from '../../utils/locationUtil';
import { Filter } from '../mockServices/mockServiceTypes';
import { pollTrackingService } from '../../axios/axios-polling';
import { getErrorMessage } from '../../utils/miscUtil';
import { getMockServiceListThunk } from '../mockServices/mockServiceList/mockServiceListActions';
import { CraneLocation, LOCATION_MOCK_SERVICES } from '../../types/rest/locationTypes';
import { PROVIDER_OPL } from '../../constants/bzm-constants';
import MixpanelService from '../../utils/mixpanelService';
import {
  CREATE_MOCK_SERVICE_GROUP,
  MOCK_SERVICE_APPLY_CONFIGURATION,
  OPEN_MOCK_SERVICE_TAB,
  RECONFIGURE_MOCK_SERVICE_GROUP,
  START_MOCK_SERVICE_GROUP,
  STOP_MOCK_SERVICE_GROUP,
} from '../../utils/mixpanelEvents';

export const createBlankMockServiceGroup = (): ThunkAction<any, ApplicationState, null, any> => {
  return async (
    dispatch: ThunkDispatch<ApplicationState, any, AnyAction>,
    getState: any,
  ): Promise<MockServiceGroup> => {
    try {
      const newMockServiceName = 'MockServiceGroup-' + generateDate();
      const predefinedLocation = determineLocation(getState());

      MixpanelService.track(CREATE_MOCK_SERVICE_GROUP());

      const smGroup: MockServiceGroup = {
        configKeyAssets: [],
        assets: [],
        hits: '0',
        locationId: 0,
        serviceMocks: [],
        id: null,
        name: newMockServiceName,
        description: '',
        harborId: predefinedLocation.harborId,
        shipId: predefinedLocation.shipId,
        endpointPreference: 'HTTPS',
        preferredPort: null,
        replicas: 1,
        endpoints: [],
      };

      // Create SM Group
      const newGroup = (await acApi.groups.postMockServicesGroup(smGroup)).data.result;

      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('mockServices:successMsgs.createSuccessMsg', { name: newMockServiceName }),
        ),
      );

      dispatch(createGroup(newGroup));
      return new Promise<MockServiceGroup>(resolve => {
        resolve(newGroup);
      });
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('groups:errorMsgs.failedToCreateSmGroup'),
          error.toLocaleString(),
        ),
      );
      return new Promise<MockServiceGroup>(resolve => {
        resolve(null);
      });
    }
  };
};

export const deleteMockServiceGroup = (
  smGroup: MockServiceGroup,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      await acApi.groups.deleteMockServiceGroup(smGroup.id);
      dispatch(
        showToastNotification(ToastType.Success, i18n.t('groups:successMsgs.deleteSuccessMsg', { name: smGroup.name })),
      );
      dispatch(deleteGroup(smGroup));
    } catch (err) {
      dispatch(getMockServiceListThunk());
      const errorMsg = _.get(err, 'response.data.error');
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('groups:errorMsgs.failedToDeleteSmGroup', { name: smGroup.name }),
          errorMsg,
        ),
      );
    } finally {
      //TODO do this check for smGroup
      //dispatch(checkIfMockServiceExistInWorkspaceThunk());
    }
    //MixpanelService.track(DELETE_MOCK_SERVICE(mockService.type), true);
  };
};

export const bulkDeleteAction = (smGroups: MockServiceGroup[]): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    // Show the table-level loading animation immediately so users see feedback
    // while the async tracking completes.
    dispatch(setGroupsIsLoading(true));
    // Optimistically mark all selected groups as CONFIGURING so the per-item
    // status cell also reflects in-progress state.
    smGroups.forEach(smGroup => dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' })));

    try {
      const response = await acApi.groups.bulkMockServiceGroups('DELETE', smGroups.map(smGroup => smGroup.id));

      const error = _.get(response, 'data.error');
      if (!_.isEmpty(error)) {
        throw new Error(error);
      }
      if (!response || !response.data || !response.data.result[0] || !response.data.result[0].trackingDto.trackingId) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      try {
        const trackingId = response.data.result[0].trackingDto.trackingId;
        const trackingResult = await pollTrackingService(trackingId);
        const errors = _.get(trackingResult, 'data.result.errors');

        if (!_.isEmpty(errors)) {
          // Indicate all the group deletion got failed. Show a generic error message.
          throw new Error(errors[0]);
        }

        const successKey = smGroups.length > 1 ? 'bulkDeleteMultiple' : 'bulkDeleteSingle';
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('groups:successMsgs.' + successKey, { count: smGroups.length }),
          ),
        );
      } catch (e) {
        const errorMsg = getErrorMessage(e);
        dispatch(
          showToastNotification(
            ToastType.Danger,
            `${i18n.t('groups:errorMsgs.cannotDeleteMockServiceGroups')}  ${errorMsg}`,
            '',
          ),
        );
      } finally {
        dispatch(getMockServiceGroupList());
      }
    } catch (e) {
      const errorMsg = getErrorMessage(e);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('groups:errorMsgs.cannotDeleteMockServiceGroups')}  ${errorMsg}`,
        ),
      );
      dispatch(getMockServiceGroupList());
    }
  };
};

/**
 * Bulk START action for a list of Virtual Service Groups.
 * Sends a PATCH request with action=START, polls the returned tracking ID,
 * then dispatches a success or danger toast and refreshes the group list.
 * @param smGroups - groups to start
 */
export const bulkStartAction = (smGroups: MockServiceGroup[]): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    // Optimistically set all selected groups to CONFIGURING so the per-item
    // status cell reflects in-progress state without blocking the whole screen.
    // Screen-level loading (setGroupsIsLoading) is intentionally omitted here —
    // only bulk DELETE uses it; START/STOP use per-row configuring animation instead.
    smGroups.forEach(smGroup => dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' })));

    try {
      const response = await acApi.groups.bulkMockServiceGroups('START', smGroups.map(smGroup => smGroup.id));

      const error = _.get(response, 'data.error');
      if (!_.isEmpty(error)) {
        throw new Error(error);
      }
      if (!response || !response.data || !response.data.result[0] || !response.data.result[0].trackingDto.trackingId) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      try {
        const trackingId = response.data.result[0].trackingDto.trackingId;
        const trackingResult = await pollTrackingService(trackingId);
        const errors = _.get(trackingResult, 'data.result.errors');

        if (!_.isEmpty(errors)) {
          throw new Error(errors[0]);
        }

        const successKey = smGroups.length > 1 ? 'bulkStartMultiple' : 'bulkStartSingle';
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('groups:successMsgs.' + successKey, { count: smGroups.length }),
          ),
        );
      } catch (e) {
        const errorMsg = getErrorMessage(e);
        dispatch(
          showToastNotification(
            ToastType.Danger,
            `${i18n.t('groups:errorMsgs.cannotStartMockServiceGroups')}  ${errorMsg}`,
            '',
          ),
        );
      } finally {
        dispatch(getMockServiceGroupList());
      }
    } catch (e) {
      const errorMsg = getErrorMessage(e);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('groups:errorMsgs.cannotStartMockServiceGroups')}  ${errorMsg}`,
        ),
      );
      dispatch(getMockServiceGroupList());
    }
  };
};

/**
 * Bulk STOP action for a list of Virtual Service Groups.
 * Sends a PATCH request with action=STOP, polls the returned tracking ID,
 * then dispatches a success or danger toast and refreshes the group list.
 * @param smGroups - groups to stop
 */
export const bulkStopAction = (smGroups: MockServiceGroup[]): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    // Optimistically set all selected groups to CONFIGURING so the per-item
    // status cell reflects in-progress state without blocking the whole screen.
    // Screen-level loading (setGroupsIsLoading) is intentionally omitted here —
    // only bulk DELETE uses it; START/STOP use per-row configuring animation instead.
    smGroups.forEach(smGroup => dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' })));

    try {
      const response = await acApi.groups.bulkMockServiceGroups('STOP', smGroups.map(smGroup => smGroup.id));

      const error = _.get(response, 'data.error');
      if (!_.isEmpty(error)) {
        throw new Error(error);
      }
      if (!response || !response.data || !response.data.result[0] || !response.data.result[0].trackingDto.trackingId) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      try {
        const trackingId = response.data.result[0].trackingDto.trackingId;
        const trackingResult = await pollTrackingService(trackingId);
        const errors = _.get(trackingResult, 'data.result.errors');

        if (!_.isEmpty(errors)) {
          throw new Error(errors[0]);
        }

        const successKey = smGroups.length > 1 ? 'bulkStopMultiple' : 'bulkStopSingle';
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('groups:successMsgs.' + successKey, { count: smGroups.length }),
          ),
        );
      } catch (e) {
        const errorMsg = getErrorMessage(e);
        dispatch(
          showToastNotification(
            ToastType.Danger,
            `${i18n.t('groups:errorMsgs.cannotStopMockServiceGroups')}  ${errorMsg}`,
            '',
          ),
        );
      } finally {
        dispatch(getMockServiceGroupList());
      }
    } catch (e) {
      const errorMsg = getErrorMessage(e);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('groups:errorMsgs.cannotStopMockServiceGroups')}  ${errorMsg}`,
        ),
      );
      dispatch(getMockServiceGroupList());
    }
  };
};

/**
 * Virtual Service Group Update thunk.
 * @param smGroup group
 * @param keys attributes to update with patch request
 * @param immediateStoreUpdate if true, store is updated immediately and we do not wait for the backend
 * @param ignoreStoreAfterSave if true, response from the server is not stored to redux
 */
export const updateMockServiceGroupPatch = (
  smGroup: MockServiceGroup,
  keys: (keyof MockServiceGroup)[],
  immediateStoreUpdate?: boolean,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      if (immediateStoreUpdate) {
        dispatch(updateGroup(smGroup));
      }

      const fieldsToUpdate = {};
      keys.forEach(key => {
        if (key in smGroup) {
          fieldsToUpdate[key] = smGroup[key];
        }
      });

      // Patch SM Group fields
      const patchResponse = await acApi.groups.patchMockServiceGroup(smGroup.id, fieldsToUpdate);

      // Backend object does not contain metadata
      const merged = _.merge(smGroup, patchResponse.data.result);
      dispatch(updateGroup(merged));
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('groups:errorMsgs.failedToCreateSmGroup'),
          error.response.data.error,
        ),
      );
    }
  };
};

export const getMockServiceGroupList = (): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    try {
      dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: true } });

      //Add location filter
      const filter: Filter = getState().mockServiceGroup.filter;

      // Build query params
      const params = {
        limit: -1,
        shipIds: filter && filter.shipId ? filter.shipId.split(TRANSACTIONAL_LOCATIONS_ID_SUFFIX)[0] : undefined, //removes TRANSACTIONAL_LOCATIONS_ID_SUFFIX so filters works well.
        status: filter && filter.status ? filter.status : undefined,
      };

      const response = await acApi.groups.getMockServiceGroupList(params);
      const smGroupList = response.data.result as MockServiceGroup[];

      //TODO enable SM Group tracking:

      // Initiate trackings for virtual services in configuring state
      // Check if the list was already loaded (by checking the load time stamp)
      // If the time stamp is empty, we are loading it for very first time, hence we will restart the trackings
      // otherwise tracking are not going to be initiated for virtual services
      // const applicationState = getState() as ApplicationState;
      // if (_.isNil(applicationState.mockservices.loadTimeStamp)) {
      //   await initiateTrackingsForMockServices(mockServices);
      // }

      dispatch(updateGroupList(_.orderBy(smGroupList, 'id', 'desc')));
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cannotLoadMockServices'),
        ),
      );
    } finally {
      // Belt-and-suspenders: ensure isLoading is always cleared even if the
      // API call fails (updateGroupList already clears it on success).
      dispatch(setGroupsIsLoading(false));
    }
    return null;
  };
};

export const deployMockServiceGroup = (
  smGroup: MockServiceGroup,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      // Validate virtual service location exists
      if (_.isEmpty(smGroup.harborId) || _.isEmpty(smGroup.shipId)) {
        throw new Error('Location not selected for group. Cannot deploy.');
      }

      MixpanelService.track(START_MOCK_SERVICE_GROUP());

      //Toggle status
      dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' }));

      const deployResponse = await acApi.groups.deploy(smGroup.id);
      if (!_.has(deployResponse, 'data.result.trackingId')) {
        throw new Error('Missing tracking ID');
      }
      const trackingId = deployResponse.data.result.trackingId;
      await startPolling(trackingId, dispatch, smGroup.id);
      dispatch(
        showToastNotification(
          ToastType.Success,
          `Virtual Service Group ${smGroup.name} has been started successfully.`,
        ),
      );
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Deploy Virtual Service Group', getErrorMessage(e)));
    }
  };
};

export const reconfigureMockServiceGroup = (
  smGroup: MockServiceGroup,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //Toggle status
    dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' }));
    MixpanelService.track(RECONFIGURE_MOCK_SERVICE_GROUP());

    try {
      const deployResponse = await acApi.groups.configure(smGroup.id);
      if (!_.has(deployResponse, 'data.result.trackingId')) {
        throw new Error('Missing tracking ID');
      }
      const trackingId = deployResponse.data.result.trackingId;
      await startPolling(trackingId, dispatch, smGroup.id);
      dispatch(
        showToastNotification(
          ToastType.Success,
          `Virtual Service Group ${smGroup.name} has been reconfigured successfully.`,
        ),
      );
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Reconfigure Virtual Service Group', getErrorMessage(e)));
    }
  };
};

export const stopMockServiceGroup = (
  smGroup: MockServiceGroup,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //Toggle status
    dispatch(updateGroup({ ...smGroup, status: 'CONFIGURING' }));

    MixpanelService.track(STOP_MOCK_SERVICE_GROUP());

    try {
      const deployResponse = await acApi.groups.stop(smGroup.id);
      if (!_.has(deployResponse, 'data.result.trackingId')) {
        throw new Error('Missing tracking ID');
      }
      const trackingId = deployResponse.data.result.trackingId;
      await startPolling(trackingId, dispatch, smGroup.id);
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Stop Virtual Service Group', getErrorMessage(e)));
    }
  };
};

export const startPolling = async (trackingId: string, dispatch: any, groupId: number) => {
  // Poll for up to ~10 minutes (300 retry attempts with 2 second timeout between each)
  // until the virtual service is deployed
  const trackingResult = await pollTrackingService(trackingId, 300, 2000);
  const errorMsgs: string[] = _.get(trackingResult, 'data.result.errors');

  //Get SM Group by id
  const updatedGroup = (await acApi.groups.getById(groupId)).data.result;
  dispatch(updateGroup(updatedGroup));

  if (!_.isNil(errorMsgs) && _.size(errorMsgs) !== 0) {
    throw new Error(errorMsgs.toString());
  }
};

/**
 * Try to preselect private location for the blank virtual service group from the
 * available private non-kubernetes locations - take first one available.
 * @param state
 */
export const determineLocation = (state: ApplicationState): BlazeLocation => {
  try {
    // If private data is on, cloud is not available, preselect any location
    const locations: CraneLocation[] = state.mockservices.locData;
    if (!_.isEmpty(locations)) {
      // Find any private location which supports transactional virtual services (PC) and is not kubernetes
      const anyTxnLocation = locations.find(
        loc =>
          loc.provider.toLowerCase() === PROVIDER_OPL.toLowerCase() &&
          !loc.isKubernetes &&
          loc.funcIds.find(fc => fc === LOCATION_MOCK_SERVICES),
      );

      if (anyTxnLocation !== undefined) {
        return { harborId: anyTxnLocation.harborId, shipId: anyTxnLocation.shipId };
      }
    }
  } catch (error) {
    console.error('Cannot determine default private location', error);
  }

  return {
    harborId: null,
    shipId: null,
  };
};
